import { useEffect, useState } from 'react';

const DEFAULT_INTERVAL_MS = 30;

export type UseTypewriterResult = {
    /** How many of the `tokenCount` tokens should be visible now. */
    visibleCount: number;
    /** Whether every token is visible. True from the first render when not animating. */
    complete: boolean;
};

/**
 * Drives a progressive reveal of an already-complete string.
 *
 * Reveals by elapsed time rather than by chaining one timer per token: a chain stalls
 * mid-sentence when the tab is backgrounded and timers are throttled, whereas reading the
 * clock catches up on the next tick.
 */
export function useTypewriter(
    tokenCount: number,
    enabled = true,
    intervalMs = DEFAULT_INTERVAL_MS
): UseTypewriterResult {
    const [visibleCount, setVisibleCount] = useState(0);

    const skip = !enabled || tokenCount === 0 || prefersReducedMotion();

    useEffect(() => {
        if (skip) {
            setVisibleCount(tokenCount);
            return;
        }

        setVisibleCount(0);
        const startedAt = Date.now();

        const timer = setInterval(() => {
            const revealed = Math.floor((Date.now() - startedAt) / intervalMs);
            setVisibleCount(Math.min(revealed, tokenCount));

            if (revealed >= tokenCount) {
                clearInterval(timer);
            }
        }, intervalMs);

        return () => clearInterval(timer);
    }, [skip, tokenCount, intervalMs]);

    return { visibleCount, complete: visibleCount >= tokenCount };
}

function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
